<template>
  <div class="confirm-account">
    <section class="confirm-account__card card">
      <h1 class="confirm-account__title">
        {{ t('myPage.auth.confirmAccount.title') }}
      </h1>
      <p class="confirm-account__requirments">
        {{ t('myPage.auth.passwordRequirements.text') }}
        <ul class="confirm-account__requirements-list">
          <li>{{ t('myPage.auth.passwordRequirements.rule1') }}</li>
          <li>{{ t('myPage.auth.passwordRequirements.rule2') }}</li>
          <li>{{ t('myPage.auth.passwordRequirements.rule3') }}</li>
          <li>{{ t('myPage.auth.passwordRequirements.rule4') }}</li>
        </ul>
      </p>
      <div
        v-if="error"
        class="confirm-account__error"
      >
        {{ error }}
      </div>
      <div
        v-if="success"
        class="confirm-account__success"
      >
        {{ t('myPage.auth.confirmAccount.success') }}
      </div>
      <div
        v-if="!success"
        class="confirm-account__form"
      >
        <Input
          v-model="password"
          type="password"
          class="confirm-account__input"
          :placeholder="t('myPage.auth.confirmAccount.passwordPlaceholder')"
          autocomplete="new-password"
          :label="t('myPage.auth.confirmAccount.password')"
          id="new-password"
          :error="passwordError"
        />
        <Input
          v-model="confirmPassword"
          type="password"
          class="confirm-account__input"
          :label="t('myPage.auth.confirmAccount.confirmPassword')"
          :placeholder="
            t('myPage.auth.confirmAccount.confirmPasswordPlaceholder')
          "
          autocomplete="new-password"
          id="confirm-password"
          :error="confirmPasswordError"
        />
        <button
          class="button button--medium button--primary"
          @click="handleConfirm"
          :disabled="isLoading || !isValid"
        >
          {{
            isLoading
              ? t('common.status.loading')
              : t('myPage.auth.confirmAccount.confirm')
          }}
        </button>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
  import { defineComponent, ref, computed, onMounted } from 'vue'
  import { useRoute, useRouter } from 'vue-router'
  import { useI18n } from 'vue-i18n'
  import { confirmNewPassword } from '@/services/userService'
  import { useBookingStore } from '@/stores/bookingStore'
  import { useAuthStore } from '@/stores/oauthStore'
  import Input from '@/components/BaseComponents/Input.vue'

  export default defineComponent({
    name: 'ConfirmAccountPage',
    components: {
      Input,
    },
    setup() {
      const route = useRoute()
      const router = useRouter()
      const { t } = useI18n()
      const bookingStore = useBookingStore()
      const authStore = useAuthStore()
      const error = ref('')
      const success = ref(false)
      const isLoading = ref(false)
      const password = ref('')
      const confirmPassword = ref('')
      const passwordError = ref('')
      const confirmPasswordError = ref('')

      const validatePasswords = () => {
        passwordError.value = ''
        confirmPasswordError.value = ''

        // Check password requirements: min 8 chars, 1 uppercase, 1 digit, 1 symbol
        const hasUpperCase = /[A-Z]/.test(password.value)
        const hasDigit = /\d/.test(password.value)
        const hasSymbol = /[^A-Za-z0-9]/.test(password.value)

        if (
          password.value.length < 8 ||
          !hasUpperCase ||
          !hasDigit ||
          !hasSymbol
        ) {
          passwordError.value = t(
            'myPage.auth.confirmAccount.passwordRequirements'
          )
          return false
        }

        if (password.value !== confirmPassword.value) {
          confirmPasswordError.value = t(
            'myPage.auth.confirmAccount.passwordsDoNotMatch'
          )
          return false
        }

        return true
      }

      const isValid = computed(() => {
        const hasUpperCase = /[A-Z]/.test(password.value)
        const hasDigit = /\d/.test(password.value)
        const hasSymbol = /[^A-Za-z0-9]/.test(password.value)

        return (
          password.value.length >= 8 && 
          hasUpperCase &&
          hasDigit &&
          hasSymbol &&
          password.value === confirmPassword.value
        )
      })

      const handleConfirm = async () => {

        let securityCode = route.query.code as string
        if (securityCode) {
          securityCode = decodeURIComponent(securityCode).replace(/"/g, '')
        }

        if (!securityCode) {
          error.value = t('myPage.auth.confirmAccount.invalidConfirmationLink')
          return
        }

        if (!validatePasswords()) {
          return
        }

        try {
          isLoading.value = true
          await confirmNewPassword(securityCode, password.value)
          success.value = true

          // Check if we have a pending booking
          const hasPendingBooking = localStorage.getItem('pendingBooking')
          const pendingBookingNumber = localStorage.getItem(
            'pendingBookingNumber'
          )
          const storedBookingToken = localStorage.getItem('token')

          // Redirect after 3 seconds
          setTimeout(async () => {
            if (
              hasPendingBooking === 'true' && // Ensure the value is 'true'
              pendingBookingNumber &&
              storedBookingToken
            ) {
              // Only remove the pendingBooking flag, keep the token for login
              localStorage.removeItem('pendingBooking')
              
              router.push({
                path: '/login',
                query: {
                  redirect: '/reiseplanlegger/betaling',
                  bookingNumber: pendingBookingNumber,
                  bookingStarted: 'true'
                }
              })
            } else {
              // Normal flow - redirect to login
              router.push('/login')
            }
          }, 3000)
        } catch (err: any) {
          console.error('Error confirming password:', err)
          error.value =
            err.message || t('myPage.auth.confirmAccount.errorMessage')
        } finally {
          isLoading.value = false
        }
      }

      // Only check if we have a code on mount
      onMounted(() => {
        if (!route.query.code) {
          error.value = t('myPage.auth.confirmAccount.invalidConfirmationLink')
        }
      })

      return {
        error,
        success,
        isLoading,
        password,
        confirmPassword,
        passwordError,
        confirmPasswordError,
        isValid,
        handleConfirm,
        t,
      }
    },
  })
</script>

<style lang="scss" scoped>
  .confirm-account {
    padding: 2rem;
    max-width: 600px;
    margin: auto;

    &__card {
      padding: 2rem;
    }

    &__title {
      margin-bottom: 1rem;
      text-align: left;
    }

    &__form {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__input {
      width: 100%;
    }

    &__error {
      color: $clr-error;
      margin-bottom: 1rem;
      text-align: left;
    }

    &__success {
      color: var(--color-success);
      margin-bottom: 1rem;
      text-align: left;
    }

    &__requirments{
      color: $clr-text-disable;
      @include body-medium;
      margin: 0 0 1rem 0;
    }

    &__requirements-list {
     padding-left: 0.8rem;
      margin-top: 0.5rem;

      li {
        margin-bottom: 0.25rem;

        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }
</style>